import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import Breadcrumbs from '../Components/Breadcrumbs';
import { 
    CustomInput, 
    CustomSelect, 
    CustomToggle,
    CustomTextarea
  } from '../Components/CustomFormElements';
import Button from '../Components/Buttons';
import { AlertProvider, useAlert } from '../Components/AlertContext';
import { ConfirmationProvider, useConfirmation } from '../Components/ConfirmationContext';
import { NotificationApiProvider, useNotificationApi } from '../Services/NotificationApiProvider';
import PageLoader from '../Components/PageLoader';
import { isEmpty } from '../utils/validationUtil';

function NotificationContent({canEdit}) {
    const navigate = useNavigate();
    const location = useLocation();
    const { organizationId, notificationId } = useParams();
    const { showAlert } = useAlert();
    const { showConfirmation } = useConfirmation();

    const { getNotificationById, createNotification, updateNotificationById, sendTestNotification } = useNotificationApi();

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [isNew, setIsNew] = useState(true);
    const [isDisabled, setIsDisabled] = useState(true);

    const [name, setName] = useState();
    const [criteria, setCriteria] = useState();
    const [notificationType, setNotificationType] = useState();
    const [recipients, setRecipients] = useState();
    const [isActive, setIsActive] = useState(false);

    useEffect(() => {
        const f = async () => {
            setLoading(true);
            try {
                if (notificationId !== 'new-notification') {
                    const response = await getNotificationById(notificationId);
                    setName(response.name);
                    setCriteria(response.criteria);
                    setNotificationType(response.notification_type);
                    setRecipients(response.recipients.join(', '));
                    setIsActive(response.is_active);
                }
            } catch (error) {
                setError('Error fetching notification data');
                console.error('Error fetching notification data:', error);
            }
            finally {
                setLoading(false);
            }
        }
        f();
    }, [notificationId]);

    useEffect(() => {
        setIsNew(notificationId === 'new-notification');
        setIsDisabled(!canEdit);
    }, [notificationId])

    const onTest = async () => {
        if (isEmpty(name)) {
            showAlert('fail', 'Notification name is required');
            return;
        }
        if (isEmpty(notificationType)) {
            showAlert('fail', 'Notification type is required');
            return;
        }
        if (isEmpty(criteria)) {
            showAlert('fail', 'Notification criteria is required');
            return;
        }
        if (isEmpty(recipients)) {
            showAlert('fail', 'Notification recipients are required');
            return;
        }
        const recipientsArr = recipients.split(',').map(r => r.trim());

        if (recipientsArr.length === 0) {
            showAlert('fail', 'Notification recipients are required');
            return;
        }

        const notification = {
            name: name,
            notification_type: notificationType,
            recipients: recipientsArr,
        }

        setLoading(true);

        try {
            const response = await sendTestNotification(notification);
            const successResponse = response.data?.success ?? [];
            const failedResponse = response.data?.failed ?? [];
            const successList = successResponse.join(', ');
            if (successList.length > 0) {
                showAlert('success', `Test notification sent successfully to: ${successList}`);
            }
            if (response.status === 'error' && failedResponse.length > 0) {
                const failedList = failedResponse.join(', ');
                showAlert('fail', `Failed to send test notification to: ${failedList}`);
            }
        } catch (error) {
            console.error('Error sending test notification:', error);
            showAlert('fail', 'Failed to send test notification');
        }
        finally {
            setLoading(false);
        }
    }

    const onSave = async () => {
        if (isEmpty(name)) {
            showAlert('fail', 'Notification name is required');
            return;
        }
        if (isEmpty(notificationType)) {
            showAlert('fail', 'Notification type is required');
            return;
        }
        if (isEmpty(criteria)) {
            showAlert('fail', 'Notification criteria is required');
            return;
        }
        if (isEmpty(recipients)) {
            showAlert('fail', 'Notification recipients are required');
            return;
        }

        const recipientsArr = recipients.split(',').map(r => r.trim());

        if (recipientsArr.length === 0) {
            showAlert('fail', 'Notification recipients are required');
            return;
        }
    
        const notification = {
            name: name,
            criteria: criteria,
            notification_type: notificationType,
            recipients: recipientsArr,
            is_active: isActive
        }

        setLoading(true);

        try {
            if (isNew) {
                await createNotification(notification);
                showAlert('success', 'Notification created successfully');
            } else {
                await updateNotificationById(notificationId, notification);
                showAlert('success', 'Notification updated successfully');
            }
            navigate(`/organizations/${organizationId}/notifications-hub`);
        } catch (error) {
            console.error('Error saving notification:', error);
            showAlert('fail', 'Failed to save notification');
        }
        finally {
            setLoading(false);
        }
    }

    const onCancel = () => {
        navigate(`/organizations/${organizationId}/notifications-hub`);
    };

    if (loading) {
        return <PageLoader />;
    }

    return (
        <div className="container">
            <Breadcrumbs />
            <div className="row d-flex gap">
                <div className="radius-yes p-2 bg-yes col-6">
                    <h2 className='h2-title'>{name}</h2>
                    <div className='gap d-flex flex-row-to-col flex-column'>
                        <div className='int-col-3 p-2 border-yes radius-yes full-width-on-sml-scr'>
                            <h4 className='h4-title'>Main Settings</h4>

                            <div className='input-block d-flex flex-row justify-between'>
                                <div className='input-description'>
                                    <div className='input-title'>Notification name</div>
                                    <div className='small-font'>Enter notification name</div>
                                </div>
                                <div className='align-wrapper d-flex align-center'>
                                    <CustomInput
                                        id="chat-name"
                                        label="Notification Name"
                                        placeholder="Enter notification name"
                                        type="text"
                                        icon={<i className="fa-solid fa-comments"></i>}
                                        value={name}
                                        onChange={(id, value) => setName(value)}
                                        disabled={isDisabled}
                                    />
                                </div>
                            </div>

                            <div className='input-row input-block d-flex flex-row justify-between'>
                                <div className='input-description'>
                                    <div className='input-title'>Notification Type</div>
                                    <div className='small-font'>Select notification type</div>
                                </div>
                                <div className='align-wrapper d-flex align-center'>
                                    <CustomSelect
                                        id='notification-type'
                                        label="Notification Type"
                                        options={[
                                            { value: 'TELEGRAM', label: 'Telegram' }
                                        ]}
                                        value={notificationType}
                                        onChange={(value) => {
                                            console.log(value)
                                            setNotificationType(value)
                                        }}
                                        multiSelect={false}
                                        disabled={isDisabled || !isNew}
                                    />
                                </div>
                            </div>

                            <div className='input-block d-flex flex-row justify-between'>
                                <div className='input-description'>
                                    <div className='input-title'>Criteria</div>
                                    <div className='small-font'>Add notification criteria</div>
                                </div>
                                <div className='align-wrapper d-flex align-center'>
                                    <CustomTextarea 
                                        id="criteria"
                                        label="Criteria"
                                        name="criteria"
                                        type="text"
                                        addedText={criteria}
                                        onChange={(value) => setCriteria(value)}
                                        resizable={false}
                                        disabled={isDisabled}
                                        height="200px"
                                    />
                                </div>
                            </div>


                            <div className='input-row input-block d-flex flex-row justify-between'>
                                <div className='input-description'>
                                    <div className='input-title'>Recipients</div>
                                    <div className='small-font'>Enter recipients separated by commas</div>
                                </div>
                                <div className='align-wrapper d-flex align-center'>
                                    <CustomInput
                                        id="notification-recipients"
                                        label="Recipients"
                                        placeholder="Enter recipients"
                                        value={recipients}
                                        onChange={(id, value) => {
                                            setRecipients(value);
                                        }}
                                        type="text"
                                        disabled={isDisabled}
                                    />
                                </div>
                            </div>

                            <div className='input-row input-block d-flex flex-row justify-between'>
                                <div className='input-description'>
                                    <div className='input-title'>Activate Notification</div>
                                    <div className='small-font'>Set notification to be active or inactive to enable or disable it</div>
                                </div>
                                <div className='align-wrapper d-flex align-center'>
                                    <CustomToggle
                                        id="toggle-status"
                                        label={isActive ? 'Active' : 'Inactive'}
                                        checked={isActive}
                                        onChange={(id, value) => setIsActive(value)}
                                        disabled={false}
                                    />
                                </div>
                            </div>
                        </div>

                        <div className='buttons-container d-flex justify-end'>
                            <Button variant='primary' size='medium' disabled={isDisabled} onClick={onSave}>
                                Save
                            </Button>
                            <Button variant="primary" size='medium' disabled={isDisabled} onClick={onTest}>
                                Test
                            </Button>
                            <Button variant='danger' size='medium' onClick={onCancel}>
                                Cancel
                            </Button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}

function Notification({canEdit}) {
    return (        
        <AlertProvider>
            <ConfirmationProvider>
                <NotificationApiProvider>
                    <NotificationContent canEdit={canEdit}/>
                </NotificationApiProvider>
            </ConfirmationProvider>
        </AlertProvider>
    );
}

export default Notification;
